import { useFormatters } from '@/hooks/useFormatters';
import { useSuspenseQuery } from '@apollo/client';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Launch } from '@mui/icons-material';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
  timelineItemClasses,
} from '@mui/lab';
import { Box, Chip, Divider, IconButton, Typography } from '@mui/material';
import { type FC } from 'react';
import { getAlertSuccessTranslation } from '@/utils/i18n/getAlertSuccessOptions';
import SectionTitle from './common/SectionTitle';
import { getDeviceAlertMetricAttributes } from '@/utils/attributes/deviceAlertMetricName';
import { alertConfigurationToString } from '@/utils/alerts';
import { AlertConfiguration, AlertTypeId, DeviceAlertMetricName } from '@/__generated__/graphql';
import GET_DEVICE_PROFILE_ALERTS from '../../graphql/queries/getDeviceProfileAlerts';
import GET_DEVICE_PROFILE_ALERT_TRIGGERS from '@features/deviceProfile/graphql/queries/getDeviceProfileAlertTriggers';

interface Props {
  deviceId: string;
}

const DeviceProfileAlerts: FC<Props> = ({ deviceId }) => {
  const { _ } = useLingui();
  const { data } = useSuspenseQuery(GET_DEVICE_PROFILE_ALERTS, {
    variables: {
      deviceId,
    },
  });

  const deviceConfiguredMetrics = data.device.alerts.activeConfigurations;

  if (deviceConfiguredMetrics.length === 0) {
    return (
      <Box display="flex" flexDirection="column" gap={3}>
        <SectionTitle id="alerts" title={_(msg`Alertas`)} />
        <Box>
          <Typography fontStyle="italic" color="text.secondary" fontSize="14px">
            <Trans>No hay alertas configuradas</Trans>
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <SectionTitle id="alerts" title={_(msg`Alertas`)} />
      <Box display="grid" gridTemplateColumns="repeat(3, 300px)" gap={8}>
        {deviceConfiguredMetrics.map((metric) => (
          <DeviceInfoBox key={metric.metric} deviceId={deviceId} metric={metric} />
        ))}
      </Box>
    </Box>
  );
};

const alertTypeIds: Record<DeviceAlertMetricName, AlertTypeId> = {
  [DeviceAlertMetricName.Flow]: AlertTypeId.Flow,
  [DeviceAlertMetricName.Level]: AlertTypeId.Level,
  [DeviceAlertMetricName.Intruder]: AlertTypeId.Intruder,
  [DeviceAlertMetricName.BatterySoc]: AlertTypeId.Battery,
  [DeviceAlertMetricName.SensorDistance]: AlertTypeId.SensorDistance,
};

const DeviceInfoBox: FC<{ deviceId: string; metric: AlertConfiguration }> = ({
  deviceId,
  metric,
}) => {
  const { data } = useSuspenseQuery(GET_DEVICE_PROFILE_ALERT_TRIGGERS, {
    variables: { deviceId, alertType: alertTypeIds[metric.metric] },
  });
  const { i18n } = useLingui();
  const metricAttributes = getDeviceAlertMetricAttributes(metric.metric, i18n);
  const { formatRelativeTime } = useFormatters();
  return (
    <Box key={metric.metric} sx={{ border: 1, borderColor: 'divider', p: 0.75 }}>
      <Box p={1}>
        <Box display="flex" justifyContent="space-between" mb={0.5}>
          <Typography fontSize="14px" color="text.secondary" gutterBottom>
            <Trans>Configuración alertas de {metricAttributes.label.toLowerCase()}</Trans>
          </Typography>
        </Box>
        <Box mb={0.5}>
          <Typography fontSize="12px" color="text.secondary" mt={0.5}>
            <Trans>Configurado {formatRelativeTime(metric.startAt)}</Trans>
          </Typography>
        </Box>
        <Box display="flex" flexDirection="column" gap={1}>
          {alertConfigurationToString(metric, i18n).map((line, index) => (
            <Typography key={index} fontSize="14px">
              {line}
            </Typography>
          ))}
        </Box>
      </Box>
      <Divider sx={{ my: 1 }} />
      <Box p={1}>
        <Box display="flex" justifyContent="space-between">
          <Typography fontSize="14px" color="text.secondary" gutterBottom>
            <Trans>Últimas alertas de {metricAttributes.label.toLowerCase()} gatilladas</Trans>
          </Typography>
          <IconButton
            target="_blank"
            href={`/alerts/history?deviceId=${deviceId}&alertType=${metric.metric.toLowerCase()}`}
            size="small"
          >
            <Launch fontSize="small" />
          </IconButton>
        </Box>
        <AlertsTimeline
          color={metricAttributes.color}
          alerts={data.alertsNotifications.history.nodes}
        />
      </Box>
    </Box>
  );
};

interface AlertsTimelineProps {
  color: string;
  alerts: {
    timestamp: number;
    successRate?: number | null;
  }[];
}

const AlertsTimeline: FC<AlertsTimelineProps> = ({ color, alerts }) => {
  const { i18n } = useLingui();
  const { formatDateTime } = useFormatters();

  if (alerts.length === 0)
    return (
      <Typography pt={0.2} fontSize="small" color="text.secondary" fontStyle="italic">
        <Trans>No hay alertas gatilladas</Trans>
      </Typography>
    );

  return (
    <Timeline
      sx={{
        my: 0,
        [`& .${timelineItemClasses.root}:before`]: {
          flex: 0,
          padding: 0,
        },
      }}
    >
      {alerts.map(({ timestamp, successRate }, index) => {
        const lastItem = index === alerts.length - 1;
        const { color: successColor, label } = getAlertSuccessTranslation(successRate, i18n);

        return (
          <TimelineItem>
            <TimelineSeparator>
              <TimelineDot sx={{ bgcolor: color }} />
              {!lastItem && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent>
              <Typography fontSize="14px">{formatDateTime(timestamp)}</Typography>
              <Chip label={label} size="small" color={successColor} variant="outlined" />
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
};

export default DeviceProfileAlerts;
